import React, { useEffect, useRef } from 'react';

declare global {
  interface Window { JitsiMeetExternalAPI?: any }
}

const scriptPromises: Record<string, Promise<void>> = {};
function loadJitsiScript(domain: string): Promise<void> {
  if (window.JitsiMeetExternalAPI) return Promise.resolve();
  if (scriptPromises[domain]) return scriptPromises[domain];
  scriptPromises[domain] = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = `https://${domain}/external_api.js`;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Failed to load the meeting library'));
    document.head.appendChild(s);
  });
  return scriptPromises[domain];
}

// Embeds a Jitsi meeting. Host passes a moderator `jwt`; guest joins bare.
export function JitsiCall({
  domain, roomName, jwt, displayName, onApi, onJoined,
}: {
  domain: string;
  roomName: string;
  jwt?: string;
  displayName: string;
  onApi?: (api: any) => void;
  onJoined?: () => void;
}) {
  const nodeRef = useRef<HTMLDivElement>(null);
  const apiRef = useRef<any>(null);

  useEffect(() => {
    let cancelled = false;
    loadJitsiScript(domain).then(() => {
      if (cancelled || !nodeRef.current || !window.JitsiMeetExternalAPI) return;
      const api = new window.JitsiMeetExternalAPI(domain, {
        roomName,
        jwt: jwt || undefined,
        parentNode: nodeRef.current,
        userInfo: { displayName },
        configOverwrite: {
          prejoinPageEnabled: false,
          disableDeepLinking: true,
          startWithAudioMuted: false,
          startWithVideoMuted: false,
        },
        interfaceConfigOverwrite: {
          MOBILE_APP_PROMO: false,
          SHOW_JITSI_WATERMARK: false,
          SHOW_CHROME_EXTENSION_BANNER: false,
        },
      });
      apiRef.current = api;
      api.addEventListener?.('videoConferenceJoined', () => {
        try { api.executeCommand('displayName', displayName); } catch { /* noop */ }
        onJoined?.();
      });
      onApi?.(api);
    }).catch(() => { /* surfaced by caller UI */ });
    return () => {
      cancelled = true;
      try { apiRef.current?.dispose(); } catch { /* noop */ }
      apiRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domain, roomName, jwt]);

  return <div ref={nodeRef} style={{ width: '100%', height: '100%' }} />;
}
